import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  Client,
  CreateClientRequest,
  UpdateClientRequest,
  UpdateClientSecretRequest,
  LinkUserRequest,
  ClientCredentials,
  ClientAuditLog,
  ConnectionTestResult,
  PaginatedResponse,
  ApiResponse,
  ClientQuery,
  ClientUser,
  TestingCapabilities,
  TestCreateOrderRequest,
  TestUpdateOrderRequest,
  TestCancelOrderRequest,
  TestPushInfoRequest,
  TestPushWeightRequest,
  TestPreAlertRequest,
  TestFullWorkflowRequest,
  TestResponse,
  TestExecutionSummary,
  TestConfiguration,
  TestHistory,
  BulkTestRequest,
  BulkTestResponse
} from '../models/security.models';

@Injectable({
  providedIn: 'root'
})
export class ClientIntegrationService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // Client CRUD Operations
  getAllClients(query?: ClientQuery): Observable<Client[]> {
    let params = new HttpParams();
    
    if (query?.clientType) params = params.set('clientType', query.clientType);
    if (query?.status) params = params.set('status', query.status);
    if (query?.searchTerm) params = params.set('searchTerm', query.searchTerm);
    if (query?.pageSize) params = params.set('pageSize', query.pageSize.toString());
    if (query?.pageNumber) params = params.set('pageNumber', query.pageNumber.toString());

    return this.http.get<Client[]>(`${this.apiUrl}/integration-clients`, { params });
  }

  getClient(id: string): Observable<Client> {
    return this.http.get<Client>(`${this.apiUrl}/integration-clients/${id}`);
  }

  createClient(request: CreateClientRequest): Observable<ApiResponse<{ id: string; connectionTested: boolean }>> {
    return this.http.post<ApiResponse<{ id: string; connectionTested: boolean }>>(
      `${this.apiUrl}/integration-clients`,
      request
    );
  }

  updateClient(id: string, request: UpdateClientRequest): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/integration-clients/${id}`, request);
  }

  deleteClient(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/integration-clients/${id}`);
  }

  // Client Secret Management
  updateClientSecret(id: string, request: UpdateClientSecretRequest): Observable<ApiResponse<{ connectionTested: boolean; connectionSuccessful: boolean }>> {
    return this.http.put<ApiResponse<{ connectionTested: boolean; connectionSuccessful: boolean }>>(
      `${this.apiUrl}/integration-clients/${id}/secret`,
      request
    );
  }

  // Connection Testing
  testConnection(id: string): Observable<ApiResponse<ConnectionTestResult>> {
    return this.http.post<ApiResponse<ConnectionTestResult>>(
      `${this.apiUrl}/integration-clients/${id}/test-connection`,
      {}
    );
  }

  // Client User Management
  linkUser(clientId: string, request: LinkUserRequest): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(
      `${this.apiUrl}/integration-clients/${clientId}/users`,
      request
    );
  }

  getLinkedUsers(clientId: string, page?: number, pageSize?: number): Observable<PaginatedResponse<ClientUser>> {
    let params = new HttpParams();
    
    if (page) params = params.set('page', page.toString());
    if (pageSize) params = params.set('pageSize', pageSize.toString());

    return this.http.get<PaginatedResponse<ClientUser>>(
      `${this.apiUrl}/integration-clients/${clientId}/users`,
      { params }
    );
  }

  unlinkUser(clientId: string, userId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.apiUrl}/integration-clients/${clientId}/users/${userId}`
    );
  }

  // Client Credentials Management
  generateCredentials(id: string): Observable<ApiResponse<ClientCredentials>> {
    return this.http.post<ApiResponse<ClientCredentials>>(
      `${this.apiUrl}/integration-clients/${id}/generate-credentials`,
      {}
    );
  }

  getCredentials(id: string): Observable<ApiResponse<Omit<ClientCredentials, 'apiSecretKey'>>> {
    return this.http.get<ApiResponse<Omit<ClientCredentials, 'apiSecretKey'>>>(
      `${this.apiUrl}/integration-clients/${id}/credentials`
    );
  }

  regenerateCredentials(id: string): Observable<ApiResponse<ClientCredentials>> {
    return this.http.post<ApiResponse<ClientCredentials>>(
      `${this.apiUrl}/integration-clients/${id}/regenerate-credentials`,
      {}
    );
  }

  // Client Audit Logs
  getAuditLogs(id: string, page?: number, pageSize?: number): Observable<PaginatedResponse<ClientAuditLog>> {
    let params = new HttpParams();
    
    if (page) params = params.set('page', page.toString());
    if (pageSize) params = params.set('pageSize', pageSize.toString());

    return this.http.get<PaginatedResponse<ClientAuditLog>>(
      `${this.apiUrl}/integration-clients/${id}/audit-logs`,
      { params }
    );
  }

  // Utility Methods
  validateClientName(name: string): Observable<{ isAvailable: boolean }> {
    return this.http.get<{ isAvailable: boolean }>(`${this.apiUrl}/integration-clients/validate-name`, {
      params: { name }
    });
  }

  getClientTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/integration-clients/types`);
  }

  getClientStatuses(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/integration-clients/statuses`);
  }

  // Export functionality
  exportClients(query?: ClientQuery): Observable<Blob> {
    let params = new HttpParams();
    
    if (query?.clientType) params = params.set('clientType', query.clientType);
    if (query?.status) params = params.set('status', query.status);
    if (query?.searchTerm) params = params.set('searchTerm', query.searchTerm);

    return this.http.get(`${this.apiUrl}/integration-clients/export`, {
      params,
      responseType: 'blob'
    });
  }

  exportAuditLogs(id: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/integration-clients/${id}/audit-logs/export`, {
      responseType: 'blob'
    });
  }

  // Bulk operations
  bulkUpdateStatus(clientIds: string[], status: string): Observable<ApiResponse<{ updated: number; failed: number }>> {
    return this.http.put<ApiResponse<{ updated: number; failed: number }>>(
      `${this.apiUrl}/integration-clients/bulk/status`,
      { clientIds, status }
    );
  }

  bulkTestConnections(clientIds: string[]): Observable<ApiResponse<{ results: ConnectionTestResult[] }>> {
    return this.http.post<ApiResponse<{ results: ConnectionTestResult[] }>>(
      `${this.apiUrl}/integration-clients/bulk/test-connections`,
      { clientIds }
    );
  }

  // ===============================
  // CLIENT INTEGRATION TESTING METHODS
  // ===============================

  // Public method to get client authentication token (used by components)
  getClientAuthToken(clientId: string): Observable<string> {
    return this.getClientCredentials(clientId).pipe(
      switchMap(response => {
        console.log('Client credentials response:', response); // Debug log
        
        // Handle both response formats: {success: boolean} and {retcode: number}
        const isSuccess = (response as any).retcode === 0 || (response as any).success === true;
        
        console.log('🔍 Debug - isSuccess:', isSuccess, 'response.data exists:', !!response?.data);
        console.log('🔍 Debug - retcode:', (response as any).retcode, 'success:', (response as any).success);
        
        // Check if credentials exist and response is successful
        if (response && isSuccess && response.data) {
          console.log('✅ Credentials retrieved successfully, using admin context for authentication');
          
          // Since /api/integration-clients/authenticate endpoint doesn't exist yet,
          // we'll use the admin token approach for now
          console.log('Note: Client authentication endpoint not implemented yet, using admin context');
          
          // For now, return a placeholder token that indicates we should use admin context
          // The auth interceptor will add the admin token to requests
          return of('ADMIN_TOKEN_CONTEXT');
        } else if (response && isSuccess && !response.data) {
          // Success response but no data - handle gracefully
          console.log('✅ Credentials check successful but no data returned, proceeding with admin context');
          return of('ADMIN_TOKEN_CONTEXT');
        } else if (response && !isSuccess) {
          // API returned an error response
          const errorMsg = response.message || 'Unknown error getting credentials';
          console.log('❌ API Error:', errorMsg);
          throw new Error(`API Error: ${errorMsg}`);
        } else if (!response) {
          // No response at all
          console.log('❌ No response from credentials endpoint');
          throw new Error('No response from credentials endpoint.');
        } else {
          // Fallback case
          console.log('❌ Invalid credential response format:', response);
          throw new Error('Invalid credential response format.');
        }
      })
    );
  }

  // Private method to create HTTP headers with client token
  private createClientHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  // Get testing capabilities for a specific client
  getTestingCapabilities(clientId: string): Observable<ApiResponse<any>> {
    console.log('🔍 [getTestingCapabilities] Requesting capabilities for client:', clientId);
    
    return this.http.get<ApiResponse<any>>(
      `${this.apiUrl}/ClientIntegration/${clientId}/testing-capabilities`
    ).pipe(
      catchError(error => {
        console.error('❌ [getTestingCapabilities] API error:', error);
        
        // If the testing-capabilities endpoint doesn't exist, fallback to checking credentials directly
        if (error.status === 404 && error.url?.includes('/testing-capabilities')) {
          console.log('ℹ️ [getTestingCapabilities] Testing capabilities endpoint not found, falling back to credential check');
          
          return this.getCredentials(clientId).pipe(
            map(credentialsResponse => {
              console.log('🔄 [getTestingCapabilities] Fallback credentials response:', credentialsResponse);
              
              // Transform credentials response into capabilities format
              const hasCredentials = credentialsResponse?.success === true && !!credentialsResponse?.data;
              
              return {
                success: true,
                message: 'Testing capabilities determined from credential check',
                data: {
                  ClientId: clientId,
                  ClientName: 'Unknown',
                  CanTest: hasCredentials,
                  PrimaryCredentialType: hasCredentials ? 'API Credentials' : 'None',
                  PrimaryAccount: credentialsResponse?.data?.apiAccount || 'None',
                  HasPartialCredentials: false,
                  CredentialIssues: hasCredentials ? [] : ['No credentials found'],
                  AllowedEndpoints: credentialsResponse?.data?.allowedEndpoints || [],
                  AvailableTests: {
                    CanTestCreate: hasCredentials,
                    CanTestUpdate: hasCredentials,
                    CanTestCancel: hasCredentials,
                    CanTestPushInfo: hasCredentials,
                    CanTestPushWeight: hasCredentials,
                    CanTestPreAlert: hasCredentials,
                    CanTestFullWorkflow: hasCredentials
                  }
                }
              } as ApiResponse<any>;
            }),
            catchError(fallbackError => {
              console.error('❌ [getTestingCapabilities] Fallback credential check also failed:', fallbackError);
              
              // Return a response indicating no credentials found
              return of({
                success: true,
                message: 'No credentials found for client',
                data: {
                  ClientId: clientId,
                  ClientName: 'Unknown',
                  CanTest: false,
                  PrimaryCredentialType: 'None',
                  PrimaryAccount: 'None',
                  HasPartialCredentials: false,
                  CredentialIssues: ['Unable to check credentials'],
                  AllowedEndpoints: [],
                  AvailableTests: {
                    CanTestCreate: false,
                    CanTestUpdate: false,
                    CanTestCancel: false,
                    CanTestPushInfo: false,
                    CanTestPushWeight: false,
                    CanTestPreAlert: false,
                    CanTestFullWorkflow: false
                  }
                }
              } as ApiResponse<any>);
            })
          );
        }
        
        // For other errors, let them propagate
        return throwError(() => error);
      })
    );
  }

  // Individual endpoint testing methods
  testCreateOrder(clientId: string, request: TestCreateOrderRequest): Observable<TestResponse> {
    return this.getClientAuthToken(clientId).pipe(
      switchMap(token => {
        // For now, use regular HTTP call without custom headers
        // The auth interceptor will add the admin token
        console.log('Making test request using admin context');
        return this.http.post<TestResponse>(
          `${this.apiUrl}/integration-clients/${clientId}/testing/create-order`,
          request
        );
      })
    );
  }

  testUpdateOrder(clientId: string, request: TestUpdateOrderRequest): Observable<TestResponse> {
    return this.getClientAuthToken(clientId).pipe(
      switchMap(token => {
        return this.http.post<TestResponse>(
          `${this.apiUrl}/integration-clients/${clientId}/testing/update-order`,
          request
        );
      })
    );
  }

  testCancelOrder(clientId: string, request: TestCancelOrderRequest): Observable<TestResponse> {
    return this.getClientAuthToken(clientId).pipe(
      switchMap(token => {
        return this.http.post<TestResponse>(
          `${this.apiUrl}/integration-clients/${clientId}/testing/cancel-order`,
          request
        );
      })
    );
  }

  testPushInfo(clientId: string, request: TestPushInfoRequest): Observable<TestResponse> {
    return this.getClientAuthToken(clientId).pipe(
      switchMap(token => {
        return this.http.post<TestResponse>(
          `${this.apiUrl}/integration-clients/${clientId}/testing/push-info`,
          request
        );
      })
    );
  }

  testPushWeight(clientId: string, request: TestPushWeightRequest): Observable<TestResponse> {
    return this.getClientAuthToken(clientId).pipe(
      switchMap(token => {
        return this.http.post<TestResponse>(
          `${this.apiUrl}/integration-clients/${clientId}/testing/push-weight`,
          request
        );
      })
    );
  }

  testPreAlert(clientId: string, request: TestPreAlertRequest): Observable<TestResponse> {
    return this.getClientAuthToken(clientId).pipe(
      switchMap(token => {
        return this.http.post<TestResponse>(
          `${this.apiUrl}/integration-clients/${clientId}/testing/pre-alert`,
          request
        );
      })
    );
  }

  // Full workflow testing
  testFullWorkflow(clientId: string, request: TestFullWorkflowRequest): Observable<ApiResponse<TestExecutionSummary>> {
    return this.getClientAuthToken(clientId).pipe(
      switchMap(token => {
        const headers = this.createClientHeaders(token);
        return this.http.post<ApiResponse<TestExecutionSummary>>(
          `${this.apiUrl}/integration-clients/${clientId}/testing/full-workflow`,
          request,
          { headers }
        );
      })
    );
  }

  // Bulk testing methods
  testBulkCreateOrders(clientId: string, request: BulkTestRequest): Observable<BulkTestResponse> {
    return this.getClientAuthToken(clientId).pipe(
      switchMap(token => {
        // Convert BulkTestRequest to TestCreateOrderRequest format expected by backend
        const createOrderRequest = {
          numberOfOrders: request.numberOfOrders,
          numberOfParcelsPerOrder: request.numberOfParcelsPerOrder,
          testPrefix: request.testPrefix
        };
        return this.http.post<BulkTestResponse>(
          `${this.apiUrl}/integration-clients/${clientId}/testing/create-order`,
          createOrderRequest
        );
      })
    );
  }

  // Test configuration management (for saving/loading test setups)
  saveTestConfiguration(config: Omit<TestConfiguration, 'id' | 'createdAt' | 'lastUsed'>): Observable<ApiResponse<TestConfiguration>> {
    return this.http.post<ApiResponse<TestConfiguration>>(
      `${this.apiUrl}/integration-clients/test-configurations`,
      config
    ).pipe(
      catchError(error => {
        console.warn('Test configuration endpoint not available:', error);
        // Return a simulated success response for now
        return of({
          success: true,
          message: 'Test configuration saved locally (endpoint not implemented)',
          data: { 
            ...config, 
            id: Date.now().toString(), 
            createdAt: new Date().toISOString(), 
            lastUsed: new Date().toISOString() 
          } as TestConfiguration
        } as ApiResponse<TestConfiguration>);
      })
    );
  }

  getTestConfigurations(clientId: string): Observable<TestConfiguration[]> {
    return this.http.get<TestConfiguration[]>(
      `${this.apiUrl}/integration-clients/${clientId}/test-configurations`
    ).pipe(
      catchError(error => {
        console.warn('Test configurations endpoint not available:', error);
        // Return empty array for now
        return of([]);
      })
    );
  }

  updateTestConfiguration(id: string, config: Partial<TestConfiguration>): Observable<ApiResponse<TestConfiguration>> {
    return this.http.put<ApiResponse<TestConfiguration>>(
      `${this.apiUrl}/integration-clients/test-configurations/${id}`,
      config
    ).pipe(
      catchError(error => {
        console.warn('Test configuration update endpoint not available:', error);
        return of({
          success: true,
          message: 'Test configuration updated locally (endpoint not implemented)',
          data: { ...config, id, lastUsed: new Date().toISOString() } as TestConfiguration
        } as ApiResponse<TestConfiguration>);
      })
    );
  }

  deleteTestConfiguration(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.apiUrl}/integration-clients/test-configurations/${id}`
    ).pipe(
      catchError(error => {
        console.warn('Test configuration delete endpoint not available:', error);
        return of({
          success: true,
          message: 'Test configuration deleted locally (endpoint not implemented)',
          data: undefined
        } as ApiResponse<void>);
      })
    );
  }

  // Test history and results
  getTestHistory(clientId: string, page?: number, pageSize?: number): Observable<PaginatedResponse<TestHistory>> {
    let params = new HttpParams();
    
    if (page) params = params.set('page', page.toString());
    if (pageSize) params = params.set('pageSize', pageSize.toString());

    return this.http.get<PaginatedResponse<TestHistory>>(
      `${this.apiUrl}/integration-clients/${clientId}/test-history`,
      { params }
    ).pipe(
      catchError(error => {
        console.warn('Test history endpoint not available:', error);
        // Return empty paginated response for now
        return of({
          items: [],
          totalCount: 0,
          pageSize: pageSize || 10,
          pageNumber: page || 1,
          totalPages: 0
        } as PaginatedResponse<TestHistory>);
      })
    );
  }

  exportTestResults(clientId: string, fromDate?: string, toDate?: string): Observable<Blob> {
    let params = new HttpParams();
    
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);

    return this.http.get(
      `${this.apiUrl}/integration-clients/${clientId}/test-results/export`,
      { params, responseType: 'blob' }
    );
  }

  // ===============================
  // CLIENT CREDENTIALS MANAGEMENT
  // ===============================

  // Get client credentials
  getClientCredentials(clientId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${this.apiUrl}/integration-clients/${clientId}/credentials`
    );
  }

  // Generate client credentials
  generateClientCredentials(clientId: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.apiUrl}/integration-clients/${clientId}/generate-credentials`,
      {}
    );
  }

  // Regenerate client credentials
  regenerateClientCredentials(clientId: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.apiUrl}/integration-clients/${clientId}/regenerate-credentials`,
      {}
    );
  }

  // Utility methods for testing
  generateTestData(testType: string, clientType: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/integration-clients/testing/generate-test-data`,
      { testType, clientType }
    );
  }

  validateTestData(testType: string, data: any): Observable<{ isValid: boolean; errors: string[] }> {
    return this.http.post<{ isValid: boolean; errors: string[] }>(
      `${this.apiUrl}/integration-clients/testing/validate-test-data`,
      { testType, data }
    );
  }

  // Test execution methods
  executeCreateOrderTest(): Observable<TestResponse> {
    const adminToken = this.getAdminToken();
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (adminToken) {
      headers = headers.set('Authorization', `Bearer ${adminToken}`);
    }

    // Fallback implementation - create test data and log it
    const testData = {
      orderId: `TEST-${Date.now()}`,
      clientId: 'test-client',
      testType: 'create-order',
      timestamp: new Date().toISOString()
    };

    console.log('Executing create order test with data:', testData);
    
    return this.http.post<TestResponse>(
      `${this.apiUrl}/integration-clients/testing/execute-create-order`,
      testData,
      { headers }
    ).pipe(
      catchError(() => {
        // Graceful fallback for development
        const mockResponse: TestResponse = {
          success: true,
          message: 'Create order test executed (mock response)',
          data: {
            testData: testData,
            Summary: {
              TotalOrdersRequested: 1,
              SuccessfullyCreated: 1,
              Failed: 0,
              ParcelsPerOrder: 1,
              TotalParcelsCreated: 1,
              TestPrefix: 'TEST',
              CredentialType: 'Mock',
              UsedAccount: 'test-account'
            }
          }
        };
        console.log('Mock create order test response:', mockResponse);
        return of(mockResponse);
      })
    );
  }

  executeUpdateOrderTest(): Observable<TestResponse> {
    const adminToken = this.getAdminToken();
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (adminToken) {
      headers = headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const testData = {
      orderId: `UPDATE-TEST-${Date.now()}`,
      clientId: 'test-client',
      testType: 'update-order',
      timestamp: new Date().toISOString()
    };

    console.log('Executing update order test with data:', testData);
    
    return this.http.post<TestResponse>(
      `${this.apiUrl}/integration-clients/testing/execute-update-order`,
      testData,
      { headers }
    ).pipe(
      catchError(() => {
        const mockResponse: TestResponse = {
          success: true,
          message: 'Update order test executed (mock response)',
          data: {
            testData: testData,
            Summary: {
              TotalOrdersRequested: 1,
              SuccessfullyCreated: 1,
              Failed: 0,
              ParcelsPerOrder: 1,
              TotalParcelsCreated: 1,
              TestPrefix: 'UPDATE-TEST',
              CredentialType: 'Mock',
              UsedAccount: 'test-account'
            }
          }
        };
        console.log('Mock update order test response:', mockResponse);
        return of(mockResponse);
      })
    );
  }

  executeFullWorkflowTest(): Observable<TestResponse> {
    const adminToken = this.getAdminToken();
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (adminToken) {
      headers = headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const testData = {
      workflowId: `WORKFLOW-TEST-${Date.now()}`,
      clientId: 'test-client',
      testType: 'full-workflow',
      timestamp: new Date().toISOString()
    };

    console.log('Executing full workflow test with data:', testData);
    
    return this.http.post<TestResponse>(
      `${this.apiUrl}/integration-clients/testing/execute-full-workflow`,
      testData,
      { headers }
    ).pipe(
      catchError(() => {
        const mockResponse: TestResponse = {
          success: true,
          message: 'Full workflow test executed (mock response)',
          data: {
            testData: testData,
            Summary: {
              TotalOrdersRequested: 1,
              SuccessfullyCreated: 1,
              Failed: 0,
              ParcelsPerOrder: 1,
              TotalParcelsCreated: 1,
              TestPrefix: 'WORKFLOW-TEST',
              CredentialType: 'Mock',
              UsedAccount: 'test-account'
            }
          }
        };
        console.log('Mock full workflow test response:', mockResponse);
        return of(mockResponse);
      })
    );
  }

  private getAdminToken(): string | null {
    return localStorage.getItem('adminToken');
  }
}
